/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// javascipt plugin for creating charts
import { useState, useEffect } from "react";
import Chart from "chart.js";

// react plugin used to create charts
import { Bar, Line } from "react-chartjs-2";
// reactstrap components

import {
  Card,
  CardHeader,
  Row,
  Col,
  CardBody,
  Container,
} from "reactstrap";

// core components
import { chartOptions, parseOptions } from "variables/charts.js";

import { Store } from "StoreContext";

import moment from "moment";
import api from "api";
import { useHistory } from "react-router-dom";
import { chartRanges as sharedChartRanges, DEFAULT_RANGE, buildLabels, buildBarColors, buildPointConfig } from '../../utils/chartHelpers';

// export const options = {
//   responsive: true,
//   animation: {
//     duration: 2500,
//   },
//   datasets: {
//     bar: {
//       barThickness: 22,
//       borderRadius: {
//         topRight: 5,
//         topLeft: 5,
//       },
//     },
//   },
//   options: {
//     scales: {
//       x: {
//         grid: {
//           display: false,
//         },
//       },
//       y: {
//         border: {
//           display: false,
//         },
//         // ticks: {
//         //   stepSize: 5,
//         // },
//         suggestedMin: 0,
//         suggestedMax: 300,
//         ticks: {
//           // forces step size to be 50 units
//           stepSize: 5,
//         },
//       },
//     },
//   },
//   plugins: {
//     legend: {
//       position: "top",
//     },
//   },
// };

export const options = {
  responsive: true,
  animation: {
    duration: 2500,
  },
  datasets: {
    bar: {
      barThickness: 8,
      borderRadius: {
        topRight: 5,
        topLeft: 5,
      },
    },
  },
  options: {
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        border: {
          display: false,
        },
        // ticks: {
        //   stepSize: 5,
        // },
        suggestedMin: 0,
        suggestedMax: 300,
        ticks: {
          // forces step size to be 50 units
          stepSize: 5,
        },
      },
    },
  },
  plugins: {
    legend: {
      position: "top",
    },
  },
};

export const options2 = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  animation: {
    duration: 2500,
  },
  scales: {
    xAxes: [{
      gridLines: {
        display: false,
      },
      ticks: {
        padding: 10,
      },
      barThickness: 8,
    }],
    yAxes: [{
      gridLines: {
        display: true,
        lineWidth: 1,
        color: "rgba(0,0,0,0.1)",
        drawBorder: true,
        drawTicks: true,
      },
      ticks: {
        beginAtZero: true,
        padding: 10,
        maxTicksLimit: 5,
        callback: function(value) { return value.toLocaleString(); },
      },
    }],
  },
};

const options3 = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [{
      gridLines: {
        display: true,
        color: "rgba(0,0,0,0.1)",
        drawBorder: true,
        drawOnChartArea: true,
      },
      ticks: {
        padding: 10,
      },
    }],
    yAxes: [{
      gridLines: {
        display: true,
        lineWidth: 1,
        color: "rgba(0,0,0,0.1)",
        drawBorder: true,
        drawTicks: true,
      },
      ticks: {
        beginAtZero: true,
        padding: 10,
        maxTicksLimit: 5,
        callback: function(value) { return value.toLocaleString(); },
      },
    }],
  },
};

const BrandDashboard = () => {
  const store = Store();
  const { stats, user } = store;
  const labels = stats?.stats ? Object.keys(stats?.stats) : [];
  const history = useHistory();

  const [codesData, setCodesData] = useState();
  const [scanData, setScanData] = useState();
  const [chartRange, setChartRange] = useState(DEFAULT_RANGE);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [insights, setInsights] = useState([]);
  const [smsUsage, setSmsUsage] = useState(null);

  const getActivityColor = (activity) => {
    if (activity?.includes('Valid')) return '#28a745';
    if (activity?.includes('Invalid') || activity?.includes('Failed')) return '#dc3545';
    if (activity?.includes('Repeat')) return '#fd7e14';
    if (activity?.includes('Login')) return '#362FD8';
    if (activity?.includes('Points') || activity?.includes('Redeemed')) return '#6f42c1';
    if (activity?.includes('Password') || activity?.includes('Brand')) return '#17a2b8';
    return '#6c757d';
  };

  const fetchRecentLogs = () => {
    setLogsLoading(true);
    api('get', '/logs/userlogs?type=scan&page=1&pageSize=5')
      .then((res) => {
        setRecentLogs(res?.userLogs || []);
      })
      .catch(console.error)
      .finally(() => setLogsLoading(false));
  };


  // (formatLabel imported from chartHelpers)

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  const data2 = {
    labels: buildLabels(codesData),
    datasets: [{
      label: "Codes Created",
      data: (codesData || []).map((item) => item.totalCodes),
      backgroundColor: buildBarColors(codesData),
      hoverBackgroundColor: "rgba(54, 47, 216, 1)",
    }],
  };

  const scanPointConfig = buildPointConfig(scanData);
  const data3 = {
    labels: buildLabels(scanData),
    datasets: [{
      label: "Scans",
      data: (scanData || []).map((item) => item?.totalValidated),
      borderColor: "rgba(54, 47, 216, 1)",
      backgroundColor: "rgba(54, 47, 216, 0.08)",
      fill: true,
      tension: 0.4,
      ...scanPointConfig,
      pointHoverRadius: 5,
      borderWidth: 2.5,
    }],
  };

  useEffect(() => {
    if (user?._id) {
      setChartsLoading(true);
      Promise.all([
        api("get", `/codes/totalCodeAnalytics?brandId=${user._id}&range=${chartRange}`)
          .then((res) => setCodesData(res?.stats)),
        api("get", `/codes/totalScanCodeAnalytics?brandId=${user._id}&range=${chartRange}`)
          .then((res) => setScanData(res?.stats)),
      ]).catch(console.error).finally(() => setChartsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRange, user?._id]);

  useEffect(() => {
    if (user?._id) {
      fetchRecentLogs();
      api('get', `/orders?brandId=${user._id}&page=1&pageSize=5`).then(res => {
        setRecentOrders(res?.orders || []);
      }).catch(console.error);
      api('get', '/insights').then(res => {
        setInsights(res?.insights || []);
      }).catch(console.error);
      api('get', '/users/sms-usage')
        .then(res => setSmsUsage(res?.usage?.[0] || null))
        .catch(() => { /* non-critical */ });
    }
    // eslint-disable-next-line
  }, [user?._id]);


  return (
    <>
      <Container fluid>
        {/* <CardHeader className="border-0">
          <h1>Key Analytics</h1>
          <Row className="justify-content-between">
            <Row className="mb-8">
              <Col className="mb-3 mb-xl-0">
                <Card className="shadow">
                  <div
                    style={{
                      paddingBlock: "1.25rem",
                    }}
                    className="bg-transparent px-4"
                  >
                    <Row className="align-items-center">
                      <div className="col">
                        <h2
                          style={{
                            color: "#1C1C1C",
                            fontSize: "14px",
                          }}
                          className="mb-0"
                        >
                          Total Codes
                        </h2>
                        <h2
                          style={{
                            color: "#1C1C1C",
                            fontSize: "24px",
                            fontWeight: 700,
                          }}
                          className="mb-0"
                        >
                          {stats?.codesCount}
                        </h2>
                      </div>
                    </Row>
                  </div>
                  <CardBody>
                    <div style={{ height: "185px" }} className="chart">
                      <Bar data={data2} options={options2} />
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col>
                <Card className="shadow">
                  <div
                    style={{
                      paddingBlock: "1.25rem",
                    }}
                    className="bg-transparent px-4"
                  >
                    <Row className="align-items-center">
                      <div className="col">
                        <h2
                          style={{
                            color: "#1C1C1C",
                            fontSize: "14px",
                          }}
                          className="mb-0"
                        >
                          Total Scans
                        </h2>
                        <h2
                          style={{
                            color: "#1C1C1C",
                            fontSize: "24px",
                            fontWeight: 700,
                          }}
                          className="mb-0"
                        >
                          {stats?.validatedCodesCount}
                        </h2>
                      </div>
                    </Row>
                  </div>
                  <CardBody>
                    <div style={{ height: "185px" }} className="chart">
                      <Line data={data3} options={options3} />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Row>
        </CardHeader> */}
        <div className="vs-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '8px' }}>
            <h2 className="vs-section-title" style={{ margin: 0 }}>Key Analytics</h2>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {sharedChartRanges.map((r) => (
                <button
                  key={r.label}
                  onClick={() => setChartRange(r.value)}
                  style={{
                    fontFamily: 'Outfit',
                    fontWeight: 600,
                    fontSize: '12px',
                    backgroundColor: chartRange === r.value ? '#362FD8' : '#F0F0F0',
                    color: chartRange === r.value ? '#fff' : '#555',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '5px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Card className="shadow" style={{ borderRadius: '12px', border: 'none', position: 'relative' }}>
              {chartsLoading && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
                  <div style={{ width: 24, height: 24, border: '3px solid #362FD8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              <div className="bg-transparent px-4 py-3">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 className="mb-1" style={{ color: '#888', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Scans</h2>
                  <button className="vs-btn-create" style={{ padding: '5px 12px', fontSize: '12px' }} onClick={() => history.push('/brand/user-logs?type=scan')}>Scan log</button>
                </div>
                <h2 className="mb-0" style={{ color: "#1C1C1C", fontSize: "28px", fontWeight: 700 }}>
                  {(scanData || []).reduce((sum, i) => sum + (i.totalValidated || 0), 0).toLocaleString()}
                </h2>
              </div>
              <CardBody style={{ paddingTop: 0 }}>
                <div style={{ height: "220px", width: "100%" }} className="chart">
                  <Line data={data3} options={options3} />
                </div>
              </CardBody>
            </Card>
            <Card className="shadow" style={{ borderRadius: '12px', border: 'none', position: 'relative' }}>
              {chartsLoading && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' }}>
                  <div style={{ width: 24, height: 24, border: '3px solid #362FD8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              <div className="bg-transparent px-4 py-3">
                <h2 className="mb-1" style={{ color: '#888', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Codes Created</h2>
                <h2 className="mb-0" style={{ color: "#1C1C1C", fontSize: "28px", fontWeight: 700 }}>
                  {(codesData || []).reduce((sum, i) => sum + (i.totalCodes || 0), 0).toLocaleString()}
                </h2>
              </div>
              <CardBody style={{ paddingTop: 0 }}>
                <div style={{ height: "220px", width: "100%" }} className="chart">
                  <Bar data={data2} options={options2} />
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* ─── Brand Insights ─── */}
        {insights.length > 0 && (
          <>
            <div className="vs-section"><h2 className="vs-section-title">Insights</h2></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              {insights.map((ins, idx) => {
                const iconMap = { clock: '', trending: '', globe: '' };
                const colorMap = { peak_scan_time: '#362FD8', batch_performance: '#00B207', geographic_anomaly: '#dc3545' };
                const color = colorMap[ins.type] || '#362FD8';

                // Build batch detail line for batch_performance
                const batchDetail = ins.type === 'batch_performance' && ins.details?.[0]
                  ? `#${ins.details[0].batchId} · ${ins.details[0].createdAt ? new Date(ins.details[0].createdAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: '2-digit'}) : ''}`
                  : null;

                return (
                  <div key={idx} style={{
                    backgroundColor: '#fff', borderRadius: '10px', padding: '14px 16px',
                    fontFamily: 'Outfit', borderLeft: `3px solid ${color}`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}>
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{ins.title}</span>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color, marginBottom: '2px' }}>
                      {ins.metric}
                    </div>
                    {batchDetail && (
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '2px' }}>{batchDetail}</div>
                    )}
                    <p style={{ fontSize: '11px', color: '#888', lineHeight: '1.4', margin: '0 0 2px' }}>
                      {ins.description}
                    </p>
                    {ins.actionText && (
                      <span
                        style={{ fontSize: '11px', color: '#362FD8', fontWeight: 500, cursor: ins.type === 'geographic_anomaly' ? 'pointer' : 'default' }}
                        onClick={() => { if (ins.type === 'geographic_anomaly') history.push('/brand/map'); }}
                      >
                        {ins.actionText}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ─── SMS Usage ─── */}
        {smsUsage && (
          <div style={{
            marginTop: '24px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '16px 20px',
            fontFamily: 'Outfit',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <h3 className="vs-section-title" style={{ margin: '0 0 10px' }}>SMS Usage</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>
                {smsUsage.sent} / {smsUsage.limit} used this month
              </span>
              <span style={{ fontSize: '12px', color: '#999' }}>
                {smsUsage.limit > 0 ? Math.min(Math.round((smsUsage.sent / smsUsage.limit) * 100), 100) : 0}%
              </span>
            </div>
            <div style={{ width: '100%', height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${smsUsage.limit > 0 ? Math.min((smsUsage.sent / smsUsage.limit) * 100, 100) : 0}%`,
                height: '100%',
                backgroundColor: smsUsage.sent > smsUsage.limit ? '#dc3545' : '#362FD8',
                borderRadius: '4px',
                transition: 'width 0.5s ease',
              }} />
            </div>
            {smsUsage.overageCount > 0 && (
              <p style={{ fontSize: '13px', color: '#dc3545', margin: '8px 0 0', fontWeight: 500 }}>
                {smsUsage.overageCount} overage messages (${(smsUsage.overageCost / 100).toFixed(2)})
              </p>
            )}
            <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0' }}>
              {smsUsage.limit} free/month · ${(smsUsage.overageRate / 100).toFixed(2)}/SMS after
            </p>
          </div>
        )}

        <div style={{ marginTop: '24px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="vs-section-title" style={{ margin: 0 }}>Recent Orders</h3>
          <button className="vs-btn-create" style={{ padding: '7px 14px', fontSize: '13px' }} onClick={() => history.push('/brand/orders')}>See all</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {recentOrders?.map(order => {
            const codeType = order.code_type || 'regular';
            const codeTypeLabel = codeType === 'nfc' ? 'NFC' : codeType === 'codeless' ? 'Codeless' : 'Regular';
            const codeTypeColor = codeType === 'nfc' ? '#362FD8' : codeType === 'codeless' ? '#E65100' : '#00B207';
            const hasPending = order.batchIds?.some(b => b.status === 'pending');
            return (
              <Card key={order._id} className="shadow" style={{ fontFamily: 'Outfit', borderRadius: '12px', border: 'none', cursor: 'pointer' }} onClick={() => history.push('/brand/orders')}>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontWeight: 600, fontSize: '15px', color: '#1C1C1C' }}>
                        {order.description || `Order #${order._id?.slice(-6).toUpperCase()}`}
                      </span>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500,
                        backgroundColor: `${codeTypeColor}15`, color: codeTypeColor,
                      }}>{codeTypeLabel}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
                        backgroundColor: hasPending ? 'rgba(255,0,0,0.08)' : 'rgba(0,178,7,0.08)',
                        color: hasPending ? '#dc3545' : '#00B207',
                      }}>{hasPending ? 'Pending' : 'Approved'}</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>{moment(order.createdAt).format('MMM DD, YYYY')}</span>
                    </div>
                  </div>
                  {order.batchIds?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {order.batchIds.map(batch => (
                        <div key={batch._id} style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '13px',
                        }}>
                          <span style={{ fontWeight: 500, color: '#333' }}>{batch.product_name || batch.name || 'Untitled'}</span>
                          <span style={{ color: '#999', fontSize: '12px' }}>{batch.number_of_codes?.toLocaleString() || 0} codes</span>
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            backgroundColor: batch.status === 'approved' ? '#00B207' : batch.status === 'processing' ? '#362FD8' : '#dc3545',
                          }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
          {(!recentOrders || recentOrders.length === 0) && (
            <Card className="shadow" style={{ borderRadius: '12px', border: 'none' }}>
              <div style={{ padding: '24px', textAlign: 'center', color: '#888', fontFamily: 'Outfit' }}>No orders yet</div>
            </Card>
          )}
        </div>

        <div style={{ marginTop: '24px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="vs-section-title" style={{ margin: 0 }}>Recent Activity</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={fetchRecentLogs}
              style={{
                background: 'none',
                border: '1px solid #E4E4E4',
                borderRadius: '6px',
                padding: '6px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontFamily: 'Outfit',
                fontSize: '13px',
                color: '#362FD8',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#362FD8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: logsLoading ? 'spin 1s linear infinite' : 'none' }}>
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Refresh
            </button>
            <button className="vs-btn-create" style={{ padding: '7px 14px', fontSize: '13px' }} onClick={() => history.push('/brand/user-logs')}>See all</button>
          </div>
        </div>
        <Card className="shadow" style={{ fontFamily: 'Outfit', borderRadius: '12px', border: 'none' }}>
          <div style={{ padding: '0' }}>
            {recentLogs?.length > 0 ? recentLogs.map((log) => {
              const meta = log.metadata;
              const isScan = log.emailType?.includes('Scan') || log.emailType?.includes('Valid');
              const subtitle = isScan
                ? [meta?.batchName, meta?.batchId ? `#${meta.batchId}` : '', meta?.location ? [meta.location.city, meta.location.region].filter(Boolean).join(', ') : ''].filter(Boolean).join(' · ')
                : log.description;

              return (
                <div
                  key={log._id}
                  onClick={() => setSelectedLog(log)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '14px 20px',
                    borderBottom: '1px solid #f5f5f5',
                    gap: '12px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <span style={{
                    fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '4px',
                    backgroundColor: getActivityColor(log.emailType), color: '#fff',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    {log.emailType}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {meta?.code || (typeof log.description === 'object' ? JSON.stringify(log.description) : log.description)}
                    </div>
                    {subtitle && subtitle !== log.description && (
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {subtitle}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {moment(log.createdAt).fromNow()}
                  </span>
                </div>
              );
            }) : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>No recent activity</div>
            )}
          </div>
        </Card>

        {/* Activity detail modal */}
        {selectedLog && (
          <div onClick={() => setSelectedLog(null)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
              maxWidth: '480px', width: '90%', fontFamily: 'Outfit', maxHeight: '80vh', overflow: 'auto',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{
                  fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '4px',
                  backgroundColor: getActivityColor(selectedLog.emailType), color: '#fff',
                }}>
                  {selectedLog.emailType}
                </span>
                <span style={{ fontSize: '12px', color: '#999' }}>{moment(selectedLog.createdAt).format('MMM DD, YYYY hh:mm A')}</span>
              </div>
              <p style={{ fontSize: '14px', color: '#333', marginBottom: '16px', lineHeight: '1.5' }}>{typeof selectedLog.description === 'object' ? JSON.stringify(selectedLog.description) : selectedLog.description}</p>
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
                  {Object.entries(selectedLog.metadata).map(([key, value]) => {
                    if (value === null || value === undefined || value === '' || key === 'brandId') return null;
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
                    // Recursive flatten so nested objects (e.g. metadata.location
                    // containing { city, region, country, coords: {...} }) don't
                    // collapse to "[object Object]" via the implicit toString.
                    const flatten = (v) => {
                      if (v === null || v === undefined) return '';
                      if (typeof v !== 'object') return String(v);
                      if (Array.isArray(v)) return v.filter(x => x !== null && x !== undefined && x !== '').map(flatten).join(', ');
                      return Object.values(v).filter(x => x !== null && x !== undefined && x !== '').map(flatten).join(', ');
                    };
                    const display = flatten(value);
                    if (!display) return null;
                    return (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #f5f5f5' }}>
                        <span style={{ color: '#888', fontWeight: 500 }}>{label}</span>
                        <span style={{ color: '#333', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{display}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <button onClick={() => setSelectedLog(null)} className="vs-btn-modal-action" style={{ marginTop: '16px' }}>Close</button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </Container>
    </>
  );
};

export default BrandDashboard;
